import { Button, Input, Modal } from 'antd';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import s from './ReturnToPoolModal.module.scss';

import type { FC } from 'react';

export interface IReturnToPoolModalProps {
  open: boolean;
  isSubmitting: boolean;
  submitError: boolean;
  onSubmit: (returnNote: string) => void;
  onCancel: () => void;
}

const ReturnToPoolModal: FC<IReturnToPoolModalProps> = ({
  open,
  isSubmitting,
  submitError,
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  const [touched, setTouched] = useState(false);

  const isBlank = note.trim() === '';
  const showValidation = touched && isBlank;

  const handleConfirm = useCallback(() => {
    setTouched(true);
    if (isBlank) return;
    onSubmit(note.trim());
  }, [isBlank, note, onSubmit]);

  const handleAfterClose = useCallback(() => {
    setNote('');
    setTouched(false);
  }, []);

  return (
    <Modal
      open={open}
      title={t('tasksDashboard.returnModal.title')}
      centered
      destroyOnClose
      maskClosable={false}
      onCancel={onCancel}
      afterClose={handleAfterClose}
      footer={null}
      data-cy="return-to-pool-modal"
    >
      <div className={s['modal-body']}>
        <p className={s['modal-description']}>
          {t('tasksDashboard.returnModal.body')}
        </p>

        {submitError && (
          <p className={s['inline-error']} data-cy="return-modal-submit-error">
            {t('tasksDashboard.returnError')}
          </p>
        )}

        <div className={s['form-field']}>
          <label htmlFor="return-to-pool-note" className={s['field-label']}>
            {t('tasksDashboard.returnModal.noteLabel')}{' '}
            <span className={s.required}>*</span>
          </label>
          {showValidation && (
            <p
              className={s['field-error']}
              data-cy="return-to-pool-validation-error"
            >
              {t('tasksDashboard.returnModal.validationError')}
            </p>
          )}
          <Input.TextArea
            id="return-to-pool-note"
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
              if (!touched) setTouched(true);
            }}
            rows={4}
            placeholder={t('tasksDashboard.returnModal.placeholder')}
            status={showValidation ? 'error' : undefined}
            autoFocus
            data-cy="return-to-pool-textarea"
          />
        </div>
      </div>

      <div className={s['modal-footer']}>
        <Button onClick={onCancel} data-cy="return-to-pool-cancel-btn">
          {t('tasksDashboard.returnModal.cancel')}
        </Button>
        <Button
          type="primary"
          onClick={handleConfirm}
          loading={isSubmitting}
          disabled={isSubmitting || isBlank}
          data-cy="return-to-pool-confirm-btn"
        >
          {t('tasksDashboard.returnModal.submit')}
        </Button>
      </div>
    </Modal>
  );
};

export default ReturnToPoolModal;
